/* eslint-disable */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { fieldApiMapping } from '../config/fieldApiMaping';
import { tableApiConfig } from '../config/tableApiConfig';
import { useMultiplePo } from '../contexts/MultiplePoContext';
import { useDispatch } from 'react-redux';
import { analyticsAction } from '@/Actions';

// fetching api
export const useMultiPoApi = (fields, state, tableApiKey, editMode, viewMode, tableTriggers) => {
    const { setTableData, tableData, storeList, productList, lists, setLists, setLoading, skipRowReloadRef } = useMultiplePo();
    const dispatch = useDispatch();
    const fetchFieldData = useCallback(async (fieldId) => {
        const config = fieldApiMapping[fieldId];
        if (!config) return;
        if (config.skipFetch && config.skipFetch(state)) {
            return;
        }
        if (config.dependsOn) {
            const hasAllDependencies = config.dependsOn.every((dep) => state[dep]);
            if (!hasAllDependencies) {
                console.log(`Skipping ${fieldId}: dependencies not met`);
                return;
            }
        }
        setLoading(true);
        try {
            const request = config.requestBuilder(state);
            const response = await config.apiFunction(request);

            if (response?.statusCode === 200) {
                const transformedData = config.responseTransform(response);
                setLists((prev) => {
                    const prevList = prev?.[config.listKey];
                    if (
                        prevList &&
                        JSON.stringify(prevList) === JSON.stringify(transformedData)
                    ) {
                        return prev;
                    }
                    return {
                        ...prev,
                        [config.listKey]: transformedData,
                    };
                });
            }
        } catch (error) {
            console.error(`Error fetching ${fieldId}:`, error);
        } finally {
            setLoading(false);
        }
    }, [state]);

    useEffect(() => {
        (fields ?? [])?.forEach((field) => {
            if (field?.dataKey && fieldApiMapping[field?.id]) {
                const config = fieldApiMapping[field?.id];
                if (config?.dependsOn) {
                    const hasAllDeps = config?.dependsOn?.every((dep) => state?.[dep]);
                    if (hasAllDeps) {
                        fetchFieldData(field?.id);
                    }
                } else {
                    fetchFieldData(field?.id);
                }
            }
        });
    }, [state?.fromStore, state?.internalProcess, state?.kitchen, state?.orderType]);

    // for table headers
    const fetchTableHeaders = useCallback(async () => {
        const config = tableApiConfig?.header?.[tableApiKey?.header];
        if (!config) return;
        setLoading(true);
        try {
            if (config?.apiFunction) {
                const request = config.requestBuilder(state);
                const response = await config.apiFunction(request);

                if (response?.statusCode === 200) {
                    const { headers, apiStoreList } = config.responseTransform(response, state);
                    setTableData((prev) => ({
                        ...prev,
                        headers: headers,
                    }));
                    storeList.current = apiStoreList
                }
            } else {
                const { headers, apiStoreList } = config.responseTransform(state);
                setTableData((prev) => ({
                    ...prev,
                    headers: headers,
                }));
                storeList.current = apiStoreList
            }
        } catch (error) {
            console.error("Error fetching tableHeader:", error);
            setLoading(false);
        }
    }, [state]);

    //  for table rows

    const fetchTableRows = useCallback(async () => {
        const config = tableApiConfig?.row?.[tableApiKey?.row];
        if (!config) return;
        setLoading(true);
        try {
            const request = config.requestBuilder(state, lists);
            const response = await config.apiFunction(request);

            if (response?.statusCode === 200) {
                const transformRows = config.responseTransform(response, productList, storeList.current);
                setTableData((prev) => ({
                    ...prev,
                    rows: transformRows
                }));
            }
        } catch (error) {
            console.error("Error fetching tableRows:", error);
            setLoading(false);
        }
    }, [state, lists, tableData]);

    const headerTriggerKey = useMemo(() => {
        if (!tableTriggers?.header) return '';
        return tableTriggers.header.map((field) => state?.[field]?.id ?? state?.[field] ?? '').join('|');
    }, [tableTriggers?.header, state]);

    const rowTriggerKey = useMemo(() => {
        if (!tableTriggers?.row) return '';
        return tableTriggers.row.map((field) => state?.[field]?.id ?? state?.[field] ?? '').join('|');
    }, [tableTriggers?.row, state]);

    const hasAllRowRequiredFields = useMemo(() => {
        if (!tableTriggers?.rowRequiredFields) return true;
        return tableTriggers.rowRequiredFields.every((field) => {
            const value = state?.[field];
            return value !== null && value !== undefined && value !== '';
        });
    }, [tableTriggers?.rowRequiredFields, state]);


    useEffect(() => {
        if (editMode || viewMode) return;
        if (!tableTriggers?.header) return;

        const hasHeaderTriggerValues = tableTriggers.header.every((field) => {
            const value = state?.[field];
            return value !== null && value !== undefined && value !== '';
        });
        if (hasHeaderTriggerValues) {
            fetchTableHeaders();
        }
    }, [headerTriggerKey, editMode, viewMode, productList]);

    useEffect(() => {
        if (editMode || viewMode) return;

        if (!tableTriggers?.row) return;
        // "Keep Items" on a header change: leave the rows as they are this once.
        if (skipRowReloadRef?.current) {
            skipRowReloadRef.current = false;
            return;
        }
        if (hasAllRowRequiredFields) {
            fetchTableRows();
        }
    }, [rowTriggerKey, hasAllRowRequiredFields, editMode, viewMode]);

    return {
        fetchFieldData,
        fetchTableHeaders,
        fetchTableRows,
    };
};

